/**
 * Settings → Compliance — what respondents are told, what is recorded about them, and
 * the agreement that covers it. Story 26.6.
 *
 * Visual Reference: `design/screens/settings-compliance.html` — states default, `error`,
 * `accept`, `new-version`, `admin`, loading. PRD §7.6: "| Compliance | AI disclosure text,
 * consent block copy, IP capture mode, DPA acceptance record | Owner, Admin | M3 |".
 *
 * FOUR CARDS, EACH SAVING ON ITS OWN, as drawn. A person who changes the consent wording
 * and leaves has changed the consent wording and nothing else; one Save at the foot of
 * the page would make the IP setting's fate depend on a textarea two cards up.
 *
 * THE MINIMUM IS NOT A FIELD. The first card shows "This form uses AI to read your
 * answers." with a lock and *Always shown*, and nothing on this page — or in the API
 * behind it — stores, sends or edits that sentence: it is a constant in the embed bundle
 * (PRD §5.8's "non-removable minimum", RULE-074). What an organisation writes here is
 * drawn AFTER it. The preview inset shows the two together, built on the respondent
 * surface's own tokens so it is the line Story 26.5 renders and not a likeness of it.
 *
 * WHO ACCEPTS IS THE OWNER, as drawn and as the story reads the PRD's silence. An Admin
 * reads the record and is told who can; the API refuses an Admin's acceptance whatever
 * this page shows (RULE-009).
 *
 * THE SELECTED IP ROW is built at `--mdx-card-pad-selected` under its 2px border
 * (DECISION-182), so choosing a mode moves nothing inside the row.
 *
 * THE WORDS are `compliance-copy.ts`'s, held to the drawing by
 * `tests/compliance-copy.test.ts`.
 */
import { useCallback, useEffect, useId, useState, type ReactNode } from 'react'
import { apiGet, apiPost, apiPut, ApiFailure } from '../../lib/api'
import { DPA_OPEN, siteUrl } from '../../lib/site'
import { useShell } from '../../shell/workspace'
import { Button } from '../../ui/Button'
import { Callout } from '../../ui/Callout'
import { LockMark } from '../../ui/LockMark'
import { Skeleton } from '../../ui/Skeleton'
import { COMPOSED, DRAWN, IP_MODES, SHAPED } from './compliance-copy'

type IpMode = (typeof IP_MODES)[number]['mode']

/** `GET /v1/orgs/{oid}/compliance`, as `services/orgs/compliance.ts` builds it. */
export type ComplianceView = {
  disclosure: { text: string | null; max: number }
  consent: { copy: string | null; max: number }
  ip_capture_mode: IpMode
  dpa: {
    current: { version: number; published_on: string }
    accepted: { version: number; by: string; at: number } | null
  }
}

type Load = { status: 'loading' } | { status: 'failed'; message: string } | { status: 'ready'; view: ComplianceView }

/** "23 September 2026" — an instant, or an ISO date, as the drawing writes one. */
const longDate = (value: number | string) =>
  (typeof value === 'number' ? new Date(value) : new Date(`${value}T00:00:00Z`)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(typeof value === 'string' ? { timeZone: 'UTC' } : {}),
  })

/** "09:14" */
const clock = (at: number) => new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })

/** What a failed save says: the API's own sentence when it sent one that names the fix, else the drawn one. */
const failureOf = (thrown: unknown, fallback: string) =>
  thrown instanceof ApiFailure && thrown.status >= 400 && thrown.status < 500 ? thrown.message : fallback

export function Compliance() {
  const { org } = useShell()
  const [load, setLoad] = useState<Load>({ status: 'loading' })
  const [toast, setToast] = useState<string | null>(null)
  const orgId = org?.id ?? null

  const read = useCallback(async () => {
    if (!orgId) return
    setLoad({ status: 'loading' })
    try {
      setLoad({ status: 'ready', view: await apiGet<ComplianceView>(`/v1/orgs/${orgId}/compliance`) })
    } catch (thrown) {
      setLoad({ status: 'failed', message: failureOf(thrown, COMPOSED.loadFailed) })
    }
  }, [orgId])

  useEffect(() => {
    void read()
  }, [read])

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(timer)
  }, [toast])

  const header = (
    <header className="flex flex-col gap-mdx-10">
      <h1 className="m-0 text-mdx-h1 font-mdx-bold leading-mdx-tight tracking-mdx-h1">{DRAWN.title}</h1>
      <p className="m-0 max-w-mdx-measure-56 text-mdx-16 leading-mdx-body text-mdx-slate">{DRAWN.lead}</p>
    </header>
  )

  if (load.status === 'failed') {
    return (
      <>
        {header}
        <div className="flex max-w-mdx-measure-60 flex-col items-start gap-mdx-16">
          <Callout>{load.message}</Callout>
          <Button variant="secondary" onClick={() => void read()}>
            {COMPOSED.retry}
          </Button>
        </div>
      </>
    )
  }

  if (load.status === 'loading' || !org) {
    return (
      <>
        {header}
        <LoadingCards />
      </>
    )
  }

  const saved = (view: ComplianceView, message: string) => {
    setLoad({ status: 'ready', view })
    setToast(message)
  }
  const put = (body: Record<string, unknown>) => apiPut<ComplianceView>(`/v1/orgs/${org.id}/compliance`, body)

  return (
    <>
      {header}
      <DisclosureCard
        view={load.view}
        onSave={async (text) => saved(await put({ disclosure_text: text }), COMPOSED.disclosureSaved)}
      />
      <ConsentCard
        view={load.view}
        onSave={async (copy) => saved(await put({ consent_copy: copy }), COMPOSED.consentSaved)}
      />
      <IpCard
        view={load.view}
        onSave={async (mode) => saved(await put({ ip_capture_mode: mode }), COMPOSED.ipSaved)}
      />
      <DpaCard
        view={load.view}
        organisation={org.name}
        owner={org.role === 'owner'}
        onAccept={async (version) =>
          saved(await apiPost<ComplianceView>(`/v1/orgs/${org.id}/compliance/dpa`, { version }), COMPOSED.dpaAccepted)
        }
      />

      {/* `role="status"`: it confirms something that went right, as Profile's does. */}
      {toast ? (
        <div
          role="status"
          className="fixed bottom-mdx-24 left-1/2 z-40 flex -translate-x-1/2 items-center gap-mdx-8 rounded-mdx-control bg-mdx-ink px-mdx-16 py-mdx-12 text-mdx-15 font-mdx-semibold text-mdx-white shadow-mdx-card"
        >
          <span
            aria-hidden="true"
            className="flex size-mdx-20 items-center justify-center rounded-mdx-pill bg-mdx-hot text-mdx-11 font-mdx-bold"
          >
            ✓
          </span>
          {toast}
        </div>
      ) : null}
    </>
  )
}

/* ── the card, and the bar its button sits on ───────────────────────────── */

function Card({ title, help, children }: { title: string; help: string; children: ReactNode }) {
  return (
    <section className="flex max-w-mdx-measure-60 flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-28 shadow-mdx-card">
      <div className="flex flex-col gap-mdx-6">
        <h2 className="m-0 text-mdx-17 font-mdx-semibold tracking-mdx-h3">{title}</h2>
        <p className="m-0 text-mdx-15 leading-mdx-body text-mdx-slate">{help}</p>
      </div>
      {children}
    </section>
  )
}

/** Under a rule: at the card's right edge, and the card's full width at 375px, as each is drawn. */
function SaveBar({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-mdx-12 border-t border-mdx-line-soft pt-mdx-20 sm:flex-row sm:justify-end">{children}</div>
  )
}

const TEXTAREA =
  'min-h-mdx-textarea-min w-full min-w-0 resize-y rounded-mdx-control border bg-mdx-white px-mdx-14 py-mdx-10 ' +
  'text-mdx-15 leading-mdx-body text-mdx-ink outline-none transition-colors duration-mdx-swift ease-mdx-swift ' +
  'focus:border-mdx-brand focus:[box-shadow:var(--mdx-focus-ring)]'

const HELPER = 'text-mdx-13 leading-mdx-body text-mdx-slate'
const ALERT = 'text-mdx-13 leading-mdx-body text-mdx-error'
const LABEL = 'text-mdx-14 font-mdx-semibold leading-mdx-body text-mdx-ink'

/** A save in flight, and what it said if it failed. */
function useSave() {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const run = async (work: () => Promise<void>, fallback: string) => {
    setSaving(true)
    setError(null)
    try {
      await work()
    } catch (thrown) {
      setError(failureOf(thrown, fallback))
    } finally {
      setSaving(false)
    }
  }
  return { saving, error, run }
}

/* ── 1. what respondents are told about AI ──────────────────────────────── */

function DisclosureCard({ view, onSave }: { view: ComplianceView; onSave: (text: string | null) => Promise<void> }) {
  const [text, setText] = useState(view.disclosure.text ?? '')
  const { saving, error, run } = useSave()
  const helperId = useId()
  const own = text.replace(/\s+/g, ' ').trim()

  return (
    <Card title={DRAWN.disclosureTitle} help={DRAWN.disclosureHelp}>
      <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-8 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
        <span className="grow text-mdx-15 leading-mdx-body text-mdx-ink">{DRAWN.minimum}</span>
        <span className="flex shrink-0 items-center gap-mdx-6 text-mdx-13 leading-mdx-body text-mdx-slate">
          <LockMark size="sm" />
          {DRAWN.alwaysShown}
        </span>
      </div>

      <label className="flex flex-col gap-mdx-6">
        <span className={LABEL}>
          {DRAWN.ownLabel} <span className="font-mdx-regular text-mdx-slate">{DRAWN.optional}</span>
        </span>
        <textarea
          value={text}
          maxLength={view.disclosure.max}
          onChange={(event) => setText(event.target.value)}
          aria-describedby={helperId}
          aria-invalid={error ? true : undefined}
          className={`${TEXTAREA} ${error ? 'border-mdx-error' : 'border-mdx-line'}`}
        />
        {/* The alert takes the helper's place, as the `error` state draws it. */}
        {error ? (
          <span id={helperId} role="alert" className={ALERT}>
            {error}
          </span>
        ) : (
          <span id={helperId} className={HELPER}>
            {DRAWN.ownHelper}
          </span>
        )}
      </label>

      <div className="flex flex-col gap-mdx-8">
        <span className={LABEL}>{DRAWN.previewLabel}</span>
        {/*
          ON THE RESPONDENT SURFACE'S OWN TOKENS. `data-mdx-surface="embed"` is what
          `design/tokens.css` scopes `--mdx-embed-*` to, and the button and the line take
          the runtime's values — `runtime.css`'s `.primary` and `.disclosure` — so this is
          the line a funnel draws. The drawing's inset is an illustration with five values
          near the runtime's rather than the runtime's (the manifest lists them); the
          Design tab's preview is the live thing.
        */}
        <div
          data-mdx-surface="embed"
          className="flex flex-col gap-mdx-12 rounded-mdx-control border border-mdx-line bg-mdx-white p-mdx-20 [color:var(--mdx-embed-ink)] [font-family:var(--mdx-embed-font)]"
        >
          <span
            aria-hidden="true"
            className="self-start rounded-mdx-control px-mdx-24 py-mdx-14 text-mdx-16 font-mdx-semibold leading-none [background:var(--mdx-embed-accent)] [color:var(--mdx-embed-surface)]"
          >
            {DRAWN.previewButton}
          </span>
          <p className="m-0 flex max-w-mdx-measure-52 items-start gap-mdx-8 text-mdx-13 leading-mdx-body [color:var(--mdx-embed-slate)]">
            <svg className="mt-mdx-2 size-mdx-14 flex-none" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="6.5" stroke="currentColor" />
              <circle cx="7" cy="4.4" r="0.8" fill="currentColor" />
              <rect x="6.35" y="6" width="1.3" height="4.4" fill="currentColor" />
            </svg>
            <span>{own ? `${DRAWN.minimum} ${own}` : DRAWN.minimum}</span>
          </p>
        </div>
      </div>

      <SaveBar>
        <Button
          loading={saving ? DRAWN.saveDisclosure : undefined}
          onClick={() => void run(() => onSave(own === '' ? null : own), DRAWN.saveFailed)}
        >
          {DRAWN.saveDisclosure}
        </Button>
      </SaveBar>
    </Card>
  )
}

/* ── 2. consent wording ─────────────────────────────────────────────────── */

function ConsentCard({ view, onSave }: { view: ComplianceView; onSave: (copy: string | null) => Promise<void> }) {
  const [copy, setCopy] = useState(view.consent.copy ?? '')
  const { saving, error, run } = useSave()
  const clean = copy.replace(/\s+/g, ' ').trim()

  return (
    <Card title={DRAWN.consentTitle} help={DRAWN.consentHelp}>
      <div className="flex flex-col gap-mdx-6">
        <textarea
          value={copy}
          maxLength={view.consent.max}
          onChange={(event) => setCopy(event.target.value)}
          aria-label={DRAWN.consentTitle}
          aria-invalid={error ? true : undefined}
          className={`${TEXTAREA} ${error ? 'border-mdx-error' : 'border-mdx-line'}`}
        />
        {error ? (
          <span role="alert" className={ALERT}>
            {error}
          </span>
        ) : null}
      </div>
      <SaveBar>
        <Button
          loading={saving ? DRAWN.saveConsent : undefined}
          onClick={() => void run(() => onSave(clean === '' ? null : clean), DRAWN.saveFailed)}
        >
          {DRAWN.saveConsent}
        </Button>
      </SaveBar>
    </Card>
  )
}

/* ── 3. IP addresses ────────────────────────────────────────────────────── */

function IpCard({ view, onSave }: { view: ComplianceView; onSave: (mode: IpMode) => Promise<void> }) {
  const [mode, setMode] = useState<IpMode>(view.ip_capture_mode)
  const { saving, error, run } = useSave()
  const name = useId()

  return (
    <Card title={DRAWN.ipTitle} help={DRAWN.ipHelp}>
      <div role="radiogroup" aria-label={DRAWN.ipGroup} className="flex flex-col gap-mdx-8">
        {IP_MODES.map((option) => {
          const selected = option.mode === mode
          return (
            <label
              key={option.mode}
              className={[
                'flex cursor-pointer items-start gap-mdx-12 rounded-mdx-control',
                // DECISION-182: the row's 16px, less the pixel the second border takes.
                selected
                  ? 'border-2 border-mdx-brand bg-mdx-brand-tint p-mdx-card-pad-selected'
                  : 'border border-mdx-line bg-mdx-white p-mdx-16',
              ].join(' ')}
            >
              <input
                type="radio"
                name={name}
                value={option.mode}
                checked={selected}
                onChange={() => setMode(option.mode)}
                className="m-0 mt-mdx-3 size-mdx-18 shrink-0 cursor-pointer accent-mdx-brand"
              />
              <span className="flex min-w-0 flex-col gap-mdx-2">
                {/* The default mode's name shares its line with a chip; the other two are a line of their own, as drawn. */}
                {option.mode === 'anonymised' ? (
                  <span className="flex flex-wrap items-center gap-mdx-8">
                    <span className="text-mdx-15 font-mdx-semibold leading-mdx-body text-mdx-ink">{option.label}</span>
                    <span className="flex items-center rounded-mdx-pill bg-mdx-white px-mdx-8 py-mdx-2 text-mdx-12 font-mdx-semibold leading-mdx-body text-mdx-brand-deep">
                      {DRAWN.ipDefault}
                    </span>
                  </span>
                ) : (
                  <span className="text-mdx-15 font-mdx-semibold leading-mdx-body text-mdx-ink">{option.label}</span>
                )}
                <span className={`text-mdx-15 leading-mdx-body ${selected ? 'text-mdx-ink' : 'text-mdx-slate'}`}>
                  {option.detail}
                </span>
              </span>
            </label>
          )
        })}
      </div>
      {error ? (
        <span role="alert" className={ALERT}>
          {error}
        </span>
      ) : (
        <span className={HELPER}>{DRAWN.ipNote}</span>
      )}
      <SaveBar>
        <Button
          loading={saving ? DRAWN.saveIp : undefined}
          onClick={() => void run(() => onSave(mode), COMPOSED.saveFailedPlain)}
        >
          {DRAWN.saveIp}
        </Button>
      </SaveBar>
    </Card>
  )
}

/* ── 4. the Data Processing Agreement ───────────────────────────────────── */

function DpaCard({
  view,
  organisation,
  owner,
  onAccept,
}: {
  view: ComplianceView
  organisation: string
  owner: boolean
  onAccept: (version: number) => Promise<void>
}) {
  const [ticked, setTicked] = useState(false)
  const { saving, error, run } = useSave()
  const { current, accepted } = view.dpa
  const upToDate = accepted !== null && accepted.version >= current.version
  // The agreement can be accepted only where it is served, and only by the Owner.
  const asks = DPA_OPEN && owner && !upToDate

  const record = accepted ? (
    <div className="flex flex-wrap items-center gap-x-mdx-16 gap-y-mdx-8 rounded-mdx-control bg-mdx-ground px-mdx-16 py-mdx-12">
      <span
        aria-hidden="true"
        className="flex size-mdx-evidence-icon shrink-0 items-center justify-center rounded-mdx-control-sm bg-mdx-hot text-mdx-white"
      >
        <svg className="size-mdx-14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7.4 5.6 10.4 11.5 3.9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="grow text-mdx-15 leading-mdx-body text-mdx-ink">
        {SHAPED.acceptedBy} <strong className="font-mdx-semibold">{accepted.by}</strong>
        {SHAPED.acceptedOn(longDate(accepted.at), clock(accepted.at))}
      </span>
      <span className="shrink-0 text-mdx-13 leading-mdx-body text-mdx-slate">{SHAPED.version(accepted.version)}</span>
    </div>
  ) : null

  const ask = asks ? (
    <div className="flex flex-col gap-mdx-16">
      <label className="flex cursor-pointer items-start gap-mdx-12">
        <input
          type="checkbox"
          checked={ticked}
          onChange={(event) => setTicked(event.target.checked)}
          className="m-0 mt-mdx-3 size-mdx-18 shrink-0 cursor-pointer accent-mdx-brand"
        />
        <span className="text-mdx-15 leading-mdx-body text-mdx-ink">{SHAPED.acceptOnBehalf(organisation)}</span>
      </label>
      {error ? (
        <span role="alert" className={ALERT}>
          {error}
        </span>
      ) : null}
      <SaveBar>
        {/* Enabled because the box is ticked, as the drawing says of its own: an acceptance nobody ticked is not one. */}
        <Button
          disabled={!ticked}
          loading={saving ? (accepted ? SHAPED.acceptVersion(current.version) : DRAWN.dpaAccept) : undefined}
          onClick={() => void run(() => onAccept(current.version), COMPOSED.saveFailedPlain)}
        >
          {accepted ? SHAPED.acceptVersion(current.version) : DRAWN.dpaAccept}
        </Button>
      </SaveBar>
    </div>
  ) : null

  return (
    <Card title={DRAWN.dpaTitle} help={DRAWN.dpaHelp}>
      {/* `new-version`: the note, the question, then the record that stays on file. */}
      {asks && accepted ? (
        <div role="status" className="rounded-mdx-control bg-mdx-review-tint px-mdx-16 py-mdx-12 text-mdx-15 leading-mdx-body text-mdx-review-ink">
          {SHAPED.newVersion(current.version, longDate(current.published_on), accepted.version)}
        </div>
      ) : null}
      {accepted ? null : <span className="text-mdx-15 leading-mdx-body text-mdx-slate">{DRAWN.dpaNone}</span>}
      {ask}
      {record || !owner ? (
        <div className="flex flex-col gap-mdx-8">
          {record}
          {owner ? null : <span className={HELPER}>{DRAWN.dpaOwnerOnly}</span>}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-x-mdx-10 gap-y-mdx-6 text-mdx-15 leading-mdx-body">
        <a
          href={siteUrl('/dpa')}
          target="_blank"
          rel="noreferrer"
          className="rounded-mdx-sm text-mdx-15 font-mdx-semibold leading-mdx-body text-mdx-brand-deep underline"
        >
          {DRAWN.readDpa}
        </a>
        <span aria-hidden="true" className="text-mdx-15 leading-mdx-body text-mdx-slate">
          ·
        </span>
        <a
          href={siteUrl('/sub-processors')}
          target="_blank"
          rel="noreferrer"
          className="rounded-mdx-sm text-mdx-15 font-mdx-semibold leading-mdx-body text-mdx-brand-deep underline"
        >
          {DRAWN.subProcessors}
        </a>
      </div>
    </Card>
  )
}

/* ── loading ────────────────────────────────────────────────────────────── */

/** Four cards at their real geometry: a title, a line of help, and the shapes each one holds. */
function LoadingCards() {
  const shell = 'flex max-w-mdx-measure-60 flex-col gap-mdx-20 rounded-mdx-card border border-mdx-line bg-mdx-white p-mdx-28 shadow-mdx-card'
  const head = (
    <div className="flex flex-col gap-mdx-6">
      <Skeleton className="h-mdx-22 w-mdx-measure-34 max-w-full" />
      <Skeleton className="h-mdx-24 w-full" />
    </div>
  )
  const bar = (
    <div className="flex justify-end border-t border-mdx-line-soft pt-mdx-20">
      <Skeleton className="h-mdx-input w-mdx-row-control" />
    </div>
  )
  return (
    <div aria-busy="true" className="flex flex-col gap-mdx-20">
      <section className={shell}>
        {head}
        <Skeleton className="h-mdx-48 w-full" />
        <Skeleton className="h-mdx-textarea-min w-full" />
        <Skeleton className="h-mdx-textarea-min w-full" />
        {bar}
      </section>
      <section className={shell}>
        {head}
        <Skeleton className="h-mdx-textarea-min w-full" />
        {bar}
      </section>
      <section className={shell}>
        {head}
        <Skeleton className="h-mdx-textarea-min w-full" />
        <Skeleton className="h-mdx-72 w-full" />
        <Skeleton className="h-mdx-72 w-full" />
        {bar}
      </section>
      <section className={shell}>
        {head}
        <Skeleton className="h-mdx-48 w-full" />
        <Skeleton className="h-mdx-24 w-mdx-measure-34 max-w-full" />
      </section>
    </div>
  )
}
